import { useEffect, useState } from "react";
import { toastr } from "react-redux-toastr";
import {
  useCalificarDocumentoMutation,
  useLazyGetDocsListQuery,
} from "../services/docs";
import { Spinner } from "./Spinner";

export const ItemDocumentoValidar = ({
  doc,
  i,
  valor,
  siniestro,
  tramite_id,
}) => {
  const [
    guardaCalificacion,
    { isLoading: isLoadingCarga },
  ] = useCalificarDocumentoMutation();
  const [trigger] = useLazyGetDocsListQuery();

  // Estado local para la UI interactiva
  const [cal, setCal] = useState(0);
  const [respuesta, setRespuesta] = useState(valor[i]?.respuesta || "");
  const [comentario, setComentario] = useState(valor[i]?.comentario || "");

  // Variables de control de estado del documento
  const docData = doc.documento?.[0];
  const urlDoc = docData?.fc_ruta_documento || "";
  const isLoaded = !!docData;
  const isGraded = isLoaded && docData.fi_calificado === 1;
  const isCorrect = isGraded && docData.fi_rechazo_digital === 1;
  const isEditing = !isGraded || cal === 1;

  // Actualizar estado local y el objeto global por si el padre lo requiere
  const handleRespuesta = (val: string) => {
    setRespuesta(val);
    valor[i].respuesta = val;
  };

  const handleComentario = (e: any) => {
    setComentario(e.target.value);
    valor[i].comentario = e.target.value;
  };

  const rectificar = () => {
    setRespuesta(docData.fi_rechazo_digital === 1 ? "1" : "2");
    setComentario(docData.fc_comentarios_rechazo || "");
    setCal(1);
  };

  const guardarCalificacion = async () => {
    if (!respuesta) {
      toastr.error("Debes seleccionar si el documento es correcto o incorrecto");
      return;
    }
    if (respuesta === "2" && !comentario.trim()) {
      toastr.error("Debes agregar un comentario o motivo de rechazo");
      return;
    }

    try {
      const resp = await guardaCalificacion({
        siniestro,
        tramite_id: +tramite_id,
        documento_id: doc.id,
        calificacion_id: Number(respuesta),
        comentario: comentario,
        adicional: doc.adicional,
      }).unwrap();

      if (resp) {
        setCal(0);
        toastr.success(resp.message || "Calificación guardada");
        trigger({ siniestro, tramite: tramite_id });
      }
    } catch (err: any) {
      if (err?.status === 400 && err?.data?.message) {
        if (Array.isArray(err.data.message)) {
          err.data.message.forEach((m: string) => toastr.error(m));
        } else {
          toastr.error(err.data.message);
        }
      } else {
        toastr.error(err?.data?.message || "Ocurrió un error al guardar");
      }
    }
  };

  // Asignación dinámica de temas (Colors & Borders)
  let theme = {
    borderTop: "border-t-slate-400",
    badgeBg: "bg-slate-100 text-slate-600 border-slate-200",
    badgeDot: "bg-slate-400",
    badgeText: "Documento No Cargado",
    articleBg: "bg-slate-50/30 border-dashed border-slate-300",
  };

  if (isLoaded) {
    if (isEditing) {
      theme = {
        borderTop: "border-t-emerald-500",
        badgeBg: "bg-emerald-50 text-emerald-700 border-emerald-200",
        badgeDot: "bg-emerald-500",
        badgeText: "Documento Cargado",
        articleBg: "bg-white border-solid border-slate-200 hover:shadow-card",
      };
    } else if (isCorrect) {
      theme = {
        borderTop: "border-t-brand-600",
        badgeBg: "bg-brand-50 text-brand-800 border-brand-200",
        badgeDot: "bg-brand-600",
        badgeText: "Digital Verificado",
        articleBg: "bg-white border-solid border-slate-200 hover:shadow-card",
      };
    } else {
      theme = {
        borderTop: "border-t-amber-500",
        badgeBg: "bg-amber-50 text-amber-800 border-amber-200",
        badgeDot: "bg-amber-500",
        badgeText: "Digital Rechazado",
        articleBg: "bg-white border-solid border-slate-200 hover:shadow-card",
      };
    }
  }

  return (
    <article
      className={`card-active-border flex flex-col justify-between overflow-hidden rounded-xl border transition-all ${theme.articleBg} ${theme.borderTop}`}
    >
      {/* --- Cabecera del Documento --- */}
      <div className="border-b border-slate-100 p-4">
        <div className="mb-2 flex items-start justify-between gap-2">
          <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${theme.badgeBg}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${theme.badgeDot}`}></span>
            {theme.badgeText}
          </span>
          {isLoaded && urlDoc && (
            <a
              href={urlDoc}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded bg-brand-600 px-2.5 py-1 text-[11px] font-medium text-white shadow-xs transition hover:bg-brand-700"
            >
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path></svg>
              <span>Ver Archivo</span>
            </a>
          )}
        </div>
        <h2 className="text-xs font-bold leading-snug text-slate-900">
          {doc.fc_nombre_documento}
        </h2>
        <p className="mt-1.5 text-[11px] leading-normal text-slate-500">
          {doc.fc_descripcion_documento}
        </p>
      </div>

      {/* --- Cuerpo (Formulario o Sólo Lectura) --- */}
      <div className="flex flex-1 flex-col justify-between space-y-3 bg-slate-50/40 p-4">

        {/* Caso 1: Documento NO cargado */}
        {!isLoaded && (
          <div className="rounded-lg border border-amber-200/80 bg-amber-50/60 p-3 text-center">
            <svg className="mx-auto mb-1.5 h-6 w-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
            <p className="text-[11px] font-semibold text-slate-800">Archivo no suministrado</p>
            <p className="mt-1 text-[10px] text-slate-500">El asegurado aún no ha cargado este requerimiento.</p>
          </div>
        )}

        {/* Caso 2: Documento cargado - Formulario de Calificación */}
        {isLoaded && isEditing && (
          <div className="flex flex-col gap-3">
            <div>
              <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-slate-700">
                ¿El documento está correcto?
              </label>
              <div className="grid grid-cols-2 gap-2 text-xs font-semibold">
                <label
                  className={`flex cursor-pointer items-center justify-center gap-1.5 rounded-lg border px-3 py-2 transition ${respuesta === "1"
                      ? "border-brand-600 bg-brand-50/70 text-brand-800 ring-1 ring-brand-600"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                    }`}
                >
                  <input type="radio" className="hidden" name={`doc_${doc.id}_status`} value="1" onChange={() => handleRespuesta("1")} />
                  <svg className={`h-3.5 w-3.5 ${respuesta === "1" ? "text-brand-600" : "text-slate-400"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                  Correcto
                </label>
                <label
                  className={`flex cursor-pointer items-center justify-center gap-1.5 rounded-lg border px-3 py-2 transition ${respuesta === "2"
                      ? "border-red-500 bg-red-50/80 text-red-800 ring-1 ring-red-500"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                    }`}
                >
                  <input type="radio" className="hidden" name={`doc_${doc.id}_status`} value="2" onChange={() => handleRespuesta("2")} />
                  <svg className={`h-3.5 w-3.5 ${respuesta === "2" ? "text-red-600" : "text-slate-400"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
                  Incorrecto
                </label>
              </div>
            </div>

            <div>
              <label className="mb-1 block text-[11px] font-semibold text-slate-600">Comentarios:</label>
              <textarea
                value={comentario}
                onChange={handleComentario}
                placeholder="Observaciones técnicas o motivos de rechazo..."
                className="w-full rounded-md border-slate-300 bg-white text-xs text-slate-700 shadow-xs focus:border-brand-500 focus:ring-brand-500"
                rows={2}
              ></textarea>
            </div>
          </div>
        )}

        {/* Caso 3: Documento cargado y calificado - Sólo Lectura */}
        {isLoaded && !isEditing && (
          <div className="flex flex-col gap-3 rounded-lg border border-slate-100 bg-white p-3">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase text-slate-500">Dictamen:</span>
              <span className={`flex items-center gap-1 text-xs font-bold ${isCorrect ? "text-brand-600" : "text-amber-600"}`}>
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isCorrect ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path>}
                </svg>
                {isCorrect ? "Correcto" : "Incorrecto"}
              </span>
            </div>
            <div>
              <span className="mb-1 block text-[11px] font-bold uppercase text-slate-500">Comentarios:</span>
              <p className="text-xs text-slate-700">{docData.fc_comentarios_rechazo || "Sin observaciones."}</p>
            </div>
            <button
              onClick={rectificar}
              className="mt-1 w-full rounded-md bg-slate-100 py-1.5 text-[11px] font-semibold text-slate-600 transition hover:bg-slate-200"
            >
              Rectificar Calificación
            </button>
          </div>
        )}

        {/* --- Barra de Acción Inferior --- */}
        <div className="flex items-center justify-between border-t border-slate-100 pt-3 mt-3">
          <span className="text-[10px] font-medium text-slate-400">
            {isLoaded ? (isEditing ? "Pendiente de calificar" : "Dictamen guardado") : "Requerimiento obligatorio"}
          </span>

          {isLoaded && isEditing && (
            <button
              onClick={guardarCalificacion}
              disabled={isLoadingCarga}
              className="inline-flex items-center justify-center rounded-lg bg-brand-600 p-2 text-white shadow-sm transition hover:bg-brand-700 disabled:opacity-50"
              title="Guardar dictamen"
            >
              {isLoadingCarga ? <Spinner /> : <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"></path></svg>}
            </button>
          )}
        </div>

      </div>
    </article>
  );
};
